import { ColDef, ColGroupDef, GridReadyEvent } from "ag-grid-community";
import { IServerSideDatasource } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { DashboardTileApi } from "apis/DashboardTileApi";
import { Title } from "components";
import { AgGrid } from "components/AgGrid/AgGrid";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import DateCellRenderer from "components/AgGrid/CellRenderers/DateCellRenderer";
import PriceCellRenderer from "components/AgGrid/CellRenderers/PriceCellRenderer";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ZTooltip } from "components/ZTooltip";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { showToast } from "redux/slices/Toasts";

type PendingPriceChangesRowModel = {
    id: number;
    name: string;
    lastModified: Date;
    newSellPrice: number;
    oldSellPrice: number;
}

export const PendingPriceChanges = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const urlStoreId = searchParams.get("storeId") ? parseInt(searchParams.get("storeId")!) : null;
    const generate = searchParams.get("generate") ? searchParams.get("generate") === "true" : false;
    const [selectedStore, setSelectedStore] = useState<number | null>(urlStoreId);
    const stores = useAppSelector((state) => state.stores);

    const gridRef = useRef<AgGridReact>(null);

    const getServerSideDatasource: () => IServerSideDatasource = () => {
        return {
            getRows: async (params) => {
                params.api.showLoadingOverlay();

                console.log("[Datasource] - rows requested by grid: ", params.request);

                const response = (await DashboardTileApi.PendingPriceChanges({
                        stores: selectedStore != null ? [selectedStore] : [],
                        // keyword: gridSearch.current?.searchState?.keyword,
                        // contains: true,
                        offset: params.request.startRow,
                        limit: params.request.endRow != null && params.request.startRow != null ? params.request.endRow - params.request.startRow : 100,
                        // sortModels: params.request.sortModel,
                        // ...gridSearch.current?.filterState,
                        loadDetails: true,
                    }).then(r => {
                        // Just a patch for now, we need to handle this better in the redesign
                        if (r.data.stores[0].errors.find(e => e.toLocaleLowerCase() === "Unsupported function".toLocaleLowerCase())) {
                            dispatch(showToast({ value: ["You are running an outdated version of Z Freedom Service. Please restart Z Scheduler on the store's server and wait 15 minutes for the update to process."]}))
                        }
                        if (r.data.stores[0].errors.find(e => e.toLocaleLowerCase() === "Unable to Connect to Store".toLocaleLowerCase())) {
                            dispatch(showToast({ value: ["Unable to connect to Z Freedom Service. Please ensure that Z Freedom Service is running at the store's server."]}))
                        }
                        return r.data.stores[0].response?.data.reportData ?? { rows: [], totalRows: 0 };
                    }).catch(error => {
                        return { rows: [], totalRows: 0 };
                    }));

                if (response == null) {
                    params.fail();
                    params.api.hideOverlay();
                    return;
                }
                
                if (response.rows.length === 0) {
                    params.api.showNoRowsOverlay();
                } else {
                    params.api.hideOverlay();
                }

                params.success({
                    rowData: response.rows,
                    rowCount: response.totalRows,
                });
                
            },
        };
    };
    
    const onGridReady = useCallback((params: GridReadyEvent) => {
        if (generate && selectedStore != null) {
            // Generate the report
            generateReport();
        } else {
            gridRef.current?.api.setGridOption("rowData", []);
        }
    }, []);

    // If the user changes the selected store, start date or end date, update the URL
    useEffect(() => {
        
        if (selectedStore != null) {
            searchParams.set("storeId", selectedStore.toString());
        } else {
            searchParams.delete("storeId");
        }
        
        setSearchParams(searchParams, { replace: true });
    }, [selectedStore]);

     const pendingPriceChangesColumns: (ColDef<PendingPriceChangesRowModel> | ColGroupDef<PendingPriceChangesRowModel>)[] | null = [
        {
            headerName: "Name",
            field: "name",
            flex: 2,
            sortable: false,
        },
        {
            headerName: "Old Sell Price",
            field: "oldSellPrice",
            flex: 1,
            cellRenderer: "priceCellRenderer",
            sortable: false,
            headerClass: "ag-right-aligned-header", cellRendererParams: { align: "right" } 
        },
        {
            headerName: "New Sell Price",
            field: "newSellPrice",
            flex: 1,
            cellRenderer: "priceCellRenderer",
            sortable: false,
            headerClass: "ag-right-aligned-header", cellRendererParams: { align: "right" } 
        },
        // {
        //     headerName: "Price Change",
        //     valueGetter: (params) => `${params.data.oldSellPrice.formatAsPrice(false, true, true)} → ${params.data.newSellPrice.formatAsPrice(false, true, true)}`,
            
        //     headerClass: "ag-center-aligned-header", cellStyle: { textAlign: "center" } 
        // },
        {
            headerName: "Date Sent",
            field: "lastModified",
            flex: 2,
            cellRenderer: "dateCellRenderer",
            sortable: false,
        }
    ]
    
    function generateReport() {
        var datasource = getServerSideDatasource();
        gridRef.current?.api.setGridOption("serverSideDatasource", datasource);
    }

    return (
        <>
            <Title pageTitle="Pending Price Changes" baseCrumb={{
                name: "Dashboard",
                path: "/app/dashboards/operations"
            }} crumbs={[]} />
            <Row>
                <Col xs={12}>
                    <Card>
                        <Card.Body>
                            <Row className="d-flex align-items-end mb-1 gap-y-1">
                                <Col xs={12} sm={6}>
                                    <ZSingleSelectDropdown 
                                        options={stores.table}
                                        optionKey={s => s.id}
                                        value={stores.table.find(s => s.id === selectedStore)}
                                        display={s => s.name}
                                        onSelectionChange={s => { 
                                            setSelectedStore(s?.id ?? null);
                                        }}
                                        disabled={s => !s?.connected}
                                        searchPlaceholder="Search stores"
                                        placeholder="Select store"
                                        label="Store:"
                                        hasSearch
                                    />
                                </Col>
                            </Row>
                            <AgGridReact<PendingPriceChangesRowModel>
                                ref={gridRef}
                                columnDefs={pendingPriceChangesColumns}
                                rowModelType="serverSide"
                                pagination={true}
                                paginationPageSize={20}
                                getRowId={(p) => p.data.id.toString()}									
                                cacheBlockSize={100}
                                onGridReady={onGridReady}
                                containerStyle={{ height: "600px", width: "100%" }}
                                className="ag-theme-quartz ag-theme-custom w-100"
                                //reactiveCustomComponents
                                rowSelection="multiple"
                                suppressRowClickSelection={true}
                                suppressCellFocus={true}
                                //readOnlyEdit // This is needed because we store the data in redux, so the grid cannot update the state as the redux data is immutable, so we have to handle the updates ourselves
                                //stopEditingWhenCellsLoseFocus
                                // //onCellEditRequest={(e) => console.log("cell edit request", e)}
                                // onSelectionChanged={(e) => {
                                //     const selection = gridRef.current?.api.getServerSideSelectionState();
                                //     const nodes = selection?.toggledNodes?.map(t => gridRef.current?.api.getRowNode(t.toString())?.data);
                                //     if(stocks.selectAllFilter == true){
                                //         const matchingData = stocks.table.filter(x=> nodes?.find(e=>e.id == x.id));											
                                //         deselectedRowsUpdate(matchingData);												
                                //     } else {
                                //         //@ts-ignore
                                //         setServerSideSelectionState(selection && nodes ? {selectAll: selection.selectAll, toggledNodes: nodes} : {selectAll: false, toggledNodes: []});
                                //         selectedRowsUpdate(nodes);
                                //     }
                                // }}
                                // onRowSelected={(event: RowSelectedEvent<any, any>) => {
                                //     if(event.source != "uiSelectAll" && stocks.selectAllFilter == (event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
                                //         return;
                                //     }											
                                //     if((event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
                                //         columnCheckSelectAll();
                                //     } else {
                                //         columnCheckDeselectAll();
                                //     }
                                // }}
                                components={{
                                    baseCellRenderer: BaseCellRenderer,
                                    priceCellRenderer: PriceCellRenderer,
                                    dateCellRenderer: DateCellRenderer
                                }}
                            />
                            

                            <Row className="mt-3">
                                <Col xs={12} className="d-flex flex-reverse-row justify-content-end">
                                    {/* <TimedMessage message="Please wait while we export your report..." delay={5} enabled={exporting} className="mb-0 py-1 px-2 ms-auto" />
                                    <ZTooltip enabled={!reportGenerated} text={"Please generate a report to export"}>
                                        <Button className="ms-1 cta-button" variant="light" disabled={exporting || !reportGenerated} onClick={ExportReport}>
                                            Export
                                        </Button>
                                    </ZTooltip> */}
                                    <ZTooltip enabled={selectedStore == null} text={"Please select a store to generate report for"}>
                                        <Button className="ms-1 cta-button" disabled={selectedStore == null} onClick={() => generateReport()}>Generate</Button>
                                    </ZTooltip>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
}